<template>
  <div class="behaviour-effect">
    <div class="basic-options">
      <state-input
        :state="positionState"
        :initial-value="state.effect.position"
        :show-label="false"
        width="160px"
        :disabled="readonly"
        class="position-input"
        @input="value => updatePosition(value)"
      >
        <template slot="prepend">To</template>
      </state-input>
      <help-tooltip v-if="positionWarning" type="warning">
        {{ positionWarning.message }}
      </help-tooltip>
      <state-input
        :state="animationStateWithEligibleValues"
        :initial-value="state.effect.value"
        :synced-value="state.effect.value"
        :show-label="false"
        width="240px"
        :disabled="readonly || !state.effect.position"
        class="behaviour-input"
        @input="value => updateBehaviour(value)"
      >
        <template slot="prepend">To</template>
      </state-input>
      <help-tooltip
        v-if="!state.effect.position"
        type="warning"
      >
        An option must be selected. Data will not be saved.
      </help-tooltip>
      <el-card v-if="isOneShot" :body-style="{padding: '6px'}">
        <el-checkbox
          label="Play once"
          class="repeats-checkbox"
          :value="!state.effect.repeats"
          :disabled="!isRepeating"
          @input="v => updatePlayOnce(v)"
        />
      </el-card>
      <el-input
        v-if="hasRepeatDelay"
        type="number"
        :value="state.effect.repeatDelay"
        :disabled="readonly || !state.effect.repeats"
        class="repeat-delay-input"
        @input="v => updateRepeatDelay(v)"
      >
        <template slot="prepend">repeats every</template>
        <template slot="append">s</template>
      </el-input>
      <absolute-warning-tooltip v-if="state.effect.isAbsolute" />
    </div>
    <advanced-options-card :initial-open="!!state.effect.isAbsolute">
      <el-checkbox v-model="state.effect.isAbsolute" label="Absolute Effect" :disabled="readonly" @change="update" />
      <absolute-warning-tooltip />
    </advanced-options-card>
  </div>
</template>

<script>
import StateInput from '~lib/common/components/StateInput.vue';
import HelpTooltip from '~lib/common/components/HelpTooltip';

import AdvancedOptionsCard from './common/AdvancedOptionsCard';
import nodeMixins from './mixins/NodeMixins';

export default {
  name: 'vital-effect',
  components: {
    StateInput,
    AdvancedOptionsCard,
    HelpTooltip,
    AbsoluteWarningTooltip: {
      render() {
        return (
          <HelpTooltip type="warning" direction="top">
            Use with care. Absolute behavior effects cancel currently playing animations<br />
            but its follow up Checks, Effects, Timers, and Effect Chains will
            continue as normal.
          </HelpTooltip>
        );
      },
    },
  },
  mixins: [ nodeMixins ],
  props: [ 'states', 'readonly', 'emitter', 'ikey', 'getData', 'putData', 'control' ],
  data() {
    return {
      state: {
        effect: {
          id: null,
          repeats: true,
          value: null,
          position: null,
          repeatDelay: null,
        },
      },
    };
  },
  computed: {
    behaviourState() {
      const behaviourState = this.states.find(s => s.id === 'animation');
      return {
        ...behaviourState,
        possibleValues: behaviourState.possibleValues.filter(
          enumValue =>
            enumValue.meta &&
            (enumValue.meta.isRepeating || enumValue.meta.isOneShot),
        ),
      };
    },
    eligibleAnimationValues() {
      const animationState = this.behaviourState;
      return animationState.possibleValues.filter(
        value =>
          value.meta &&
          value.meta.compatiblePositions &&
          value.meta.compatiblePositions.includes(this.state.effect.position) &&
          (!value.meta.isMilitaryOnly || this.emitter.isMilitaryOrg || this.isMilitaryAnimation)
      );
    },
    animationStateWithEligibleValues() {
      return {
        ...this.behaviourState,
        possibleValues: this.eligibleAnimationValues,
      };
    },
    positionState() {
      const positionState = this.states.find(s => s.id === 'position');
      return positionState;
    },
    isRepeating() {
      return this.behaviourState.possibleValues.find(
        value => value.key === this.state.effect.value &&
                 value.meta && value.meta.isRepeating
      );
    },
    isOneShot() {
      return this.behaviourState.possibleValues.find(
        value => value.key === this.state.effect.value &&
                 value.meta && value.meta.isOneShot
      );
    },
    hasRepeatDelay() {
      const animationEnumValue = this.behaviourState.possibleValues.find(
        enumValue => enumValue.key === this.state.effect.value,
      );
      if (!animationEnumValue) {
        return false;
      }
      return animationEnumValue.meta.hasRepeatDelay;
    },
    isMilitaryAnimation() {
      const animationEnumValue = this.behaviourState.possibleValues.find(
        enumValue => enumValue.key === this.state.effect.value,
      );
      if (!animationEnumValue) {
        return false;
      }
      return animationEnumValue.meta.isMilitaryOnly;
    },
    positionWarning() {
      if (this.warnings && this.warnings.length) {
        for (const warning of this.warnings) {
          if (warning.metadata && warning.metadata.warningId === 'helicopterStance') {
            return warning;
          }
        }
      }
      return null;
    },
  },
  created() {
    this.state.effect = { ...this.state.effect, ...this.getData(this.ikey) };
    this.emitter.trigger('process', this.control);
  },
  methods: {
    updatePosition(position) {
      this.state.effect.position = position;
      const eligibleAnimationEnumValues = this.eligibleAnimationValues.map(
        enumValue => enumValue.key,
      );
      if (
        !eligibleAnimationEnumValues.includes(this.state.effect.value)
      ) {
        this.updateBehaviour(this.eligibleAnimationValues[0].key);
      }
      this.update();
    },
    updateBehaviour(behaviour) {
      this.state.effect.value = behaviour;
      this.state.effect.oneShotValue = this.getOneShotValue(behaviour);
      this.state.effect.repeatDelay = this.hasRepeatDelay ? 0 : null;
      // Adjust repeats to valid value for selection
      if (this.isOneShot && !this.isRepeating) {
        this.state.effect.repeats = false;
      }
      if (!this.isOneShot) {
        this.state.effect.repeats = true;
      }
      this.update();
    },
    getOneShotValue(behaviour) {
      const behaviourState = this.behaviourState.possibleValues.find(
        value => value.key === behaviour
      );
      return behaviourState && behaviourState.meta && behaviourState.meta.oneShotValue;
    },
    update() {
      const { effect } = this.state;
      this.putData(this.ikey, {
        type: effect.repeats ? 'ANIMATION_REPEAT' : 'ANIMATION_ONE_SHOT',
        value: effect.value,
        oneShotValue: effect.oneShotValue,
        position: effect.position,
        repeatDelay: effect.repeatDelay,
        repeats: effect.repeats,
        isAbsolute: effect.isAbsolute,
      });
      this.emitter.trigger('process', this.control);
      this.$forceUpdate();
    },
    updatePlayOnce(playOnce) {
      const repeats = !playOnce;
      this.state.effect.repeats = repeats;
      // Reset selected behaviour if it's not part of the current eligible values
      if (
        !this.behaviourState.possibleValues.find(
          v => v.key === this.state.effect.value,
        )
      ) {
        this.state.effect.value = null;
      }
      if (!repeats) {
        this.state.effect.repeatDelay = null;
      }
      this.update();
    },
    updateRepeatDelay(repeatDelay) {
      // This is a workaround to avoid the input to render a blank string
      // Vue inputs are controlled through watchers, so when the user erases
      // "0" from the input and we fallback the value to 0, the watcher does
      // not retrigger
      this.state.effect.repeatDelay = null;
      this.$nextTick(() => {
        this.state.effect.repeatDelay = parseFloat(repeatDelay) || 0;
        this.update();
      });
    },
  },
};
</script>

<style scoped>
.basic-options {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
}

.duration-input {
  width: 125px;
}

.repeat-delay-input {
  width: 200px;
}

.help-tooltip {
  margin-left: 3px;
}
</style>
